import React, { useEffect, useState } from 'react';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Search from './components/Search';
import Submit from './components/Submit';
import Users from './components/Users';
import Compensation from './components/Compensation';
import SeasonPass from './components/SeasonPass';
import SeasonPassDetail from './components/SeasonPassDetail';
import Orders from './components/Orders';
import Cases from './components/Cases';
import Corporate from './components/Corporate';
import Products from './components/Products';
import Gate from './components/Gate';
import Parking from './components/Parking';
import {
  canAccessCompensation,
  canAccessCorporate,
  canAccessGate,
  canAccessParking,
  canAccessProducts,
  canAccessUserManagement,
  getHomePage,
  isCorporate,
  isEmployee,
  isOpsScanner,
  isParking,
  isTicketTaker,
  usesSearchHome,
} from './utils/roles';

function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [user, setUser] = useState(null);
  const [submitPrefill, setSubmitPrefill] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [seasonPassQuery, setSeasonPassQuery] = useState(null);
  const [seasonPassId, setSeasonPassId] = useState(null);
  const [seasonPassReturnTo, setSeasonPassReturnTo] = useState('search');
  const [caseId, setCaseId] = useState(null);
  const [casePrefill, setCasePrefill] = useState(null);
  const [caseReturnToSeasonPassId, setCaseReturnToSeasonPassId] = useState(null);

  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentPage(getHomePage(userData));
  };

  // Ops scanner roles stay on their single page (never employee Search home)
  useEffect(() => {
    if (!user || currentPage === 'login') return;
    if (isParking(user) && currentPage !== 'parking') {
      setCurrentPage('parking');
      return;
    }
    if (isTicketTaker(user) && currentPage !== 'gate') {
      setCurrentPage('gate');
    }
  }, [user, currentPage]);

  const handleLogout = () => {
    setUser(null);
    setSubmitPrefill(null);
    setOrderId(null);
    setSeasonPassQuery(null);
    setSeasonPassId(null);
    setSeasonPassReturnTo('search');
    setCaseId(null);
    setCasePrefill(null);
    setCaseReturnToSeasonPassId(null);
    setCurrentPage('login');
  };

  const navigateTo = (page, options = {}) => {
    if (isParking(user)) {
      setCurrentPage('parking');
      return;
    }
    if (isTicketTaker(user)) {
      setCurrentPage('gate');
      return;
    }
    if (page === 'submit') {
      setSubmitPrefill(options.prefill || null);
    } else {
      setSubmitPrefill(null);
    }
    setOrderId(page === 'orders' ? options.orderId || null : null);
    setSeasonPassQuery(page === 'season-pass' ? options.seasonPassQuery || null : null);
    if (page === 'season-pass-detail') {
      setSeasonPassId(options.seasonPassId || null);
      setSeasonPassReturnTo(
        options.returnTo || (usesSearchHome(user) ? 'search' : 'season-pass')
      );
    } else if (page !== 'cases') {
      setSeasonPassId(null);
    }
    if (page === 'cases') {
      setCaseId(options.caseId || null);
      setCasePrefill(options.casePrefill || null);
      setCaseReturnToSeasonPassId(options.returnToSeasonPassId || null);
    } else {
      setCaseId(null);
      setCasePrefill(null);
      if (page !== 'season-pass-detail') {
        setCaseReturnToSeasonPassId(null);
      }
    }
    setCurrentPage(page);
  };

  return (
    <div className="App">
      {currentPage === 'login' && <Login onLogin={handleLogin} />}
      {currentPage === 'corporate' && canAccessCorporate(user) && (
        <Corporate user={user} onLogout={handleLogout} onNavigate={navigateTo} />
      )}
      {currentPage === 'gate' && canAccessGate(user) && (
        <Gate user={user} onLogout={handleLogout} onNavigate={navigateTo} />
      )}
      {currentPage === 'parking' && canAccessParking(user) && (
        <Parking user={user} onLogout={handleLogout} onNavigate={navigateTo} />
      )}
      {currentPage === 'dashboard' &&
        !isCorporate(user) &&
        !isOpsScanner(user) &&
        !usesSearchHome(user) && (
        <Dashboard user={user} onLogout={handleLogout} onNavigate={navigateTo} />
      )}
      {currentPage === 'cases' && !isCorporate(user) && !isOpsScanner(user) && (
        <Cases
          user={user}
          onLogout={handleLogout}
          onNavigate={navigateTo}
          initialCaseId={caseId}
          initialPrefill={casePrefill}
          returnToSeasonPassId={caseReturnToSeasonPassId}
        />
      )}
      {currentPage === 'search' && !isCorporate(user) && !isOpsScanner(user) && (
        <Search user={user} onLogout={handleLogout} onNavigate={navigateTo} />
      )}
      {currentPage === 'submit' &&
        !isCorporate(user) &&
        !isEmployee(user) &&
        !isOpsScanner(user) && (
        <Submit
          user={user}
          prefill={submitPrefill}
          onLogout={handleLogout}
          onNavigate={navigateTo}
        />
      )}
      {currentPage === 'season-pass' &&
        !isCorporate(user) &&
        !isEmployee(user) &&
        !isOpsScanner(user) && (
        <SeasonPass
          user={user}
          onLogout={handleLogout}
          onNavigate={navigateTo}
          initialQuery={seasonPassQuery}
        />
      )}
      {currentPage === 'season-pass-detail' &&
        !isCorporate(user) &&
        !isOpsScanner(user) && (
        <SeasonPassDetail
          user={user}
          onLogout={handleLogout}
          onNavigate={navigateTo}
          seasonPassId={seasonPassId}
          returnTo={seasonPassReturnTo}
        />
      )}
      {currentPage === 'orders' && !isCorporate(user) && !isOpsScanner(user) && (
        <Orders
          user={user}
          onLogout={handleLogout}
          onNavigate={navigateTo}
          initialOrderId={orderId}
        />
      )}
      {currentPage === 'compensation' &&
        !isCorporate(user) &&
        !isEmployee(user) &&
        !isOpsScanner(user) &&
        canAccessCompensation(user) && (
          <Compensation user={user} onLogout={handleLogout} onNavigate={navigateTo} />
        )}
      {currentPage === 'products' &&
        !isCorporate(user) &&
        !isOpsScanner(user) &&
        canAccessProducts(user) && (
          <Products user={user} onLogout={handleLogout} onNavigate={navigateTo} />
        )}
      {currentPage === 'users' &&
        !isCorporate(user) &&
        !isEmployee(user) &&
        !isOpsScanner(user) &&
        canAccessUserManagement(user) && (
          <Users user={user} onLogout={handleLogout} onNavigate={navigateTo} />
        )}
    </div>
  );
}

export default App;
